/**
 * Lock-free single-producer / single-consumer audio ring buffer over a
 * SharedArrayBuffer, carrying interleaved S16 (Int16) PCM samples.
 *
 * This is the transport that moves the core's mixed PCM (drained in the CPU
 * worker via emulator_read_audio_ffi — 2-channel S16 @ 48000 Hz; see
 * core/emulator.h and test/wasm-smoke.mjs Part 5) across to the WebAudio
 * AudioWorklet consumer that will eventually convert S16->Float32 and write the
 * output channels. The ring itself is pure, format-agnostic about channel count
 * (it just moves Int16 *samples*), and headlessly unit-tested
 * (test/audioring.test.mjs) — no AudioContext, no worklet, no browser. Actual
 * audible output is the one manual in-browser check.
 *
 * Layout over the SharedArrayBuffer:
 *   [ 0 .. HEADER_BYTES )   — control header, an Int32Array of HEADER_I32 slots
 *   [ HEADER_BYTES .. end ) — the Int16 sample payload (capacity samples)
 *
 * The producer publishes a monotonic write index and the consumer a monotonic
 * read index, both stored as Atomics in the header. To distinguish "full" from
 * "empty" without wasting a slot, the indices live in the range
 * [0, 2*capacity) (one extra "virtual" lap): available = (write - read) mod
 * 2*capacity, and a position in the payload is index mod capacity. A write that
 * would exceed the free space DROPS the overflow (newest samples not written),
 * and a read past the available count fills the remainder with SILENCE (0) —
 * i.e. underflow is silence, the right behaviour for an audio output callback
 * that must always produce a full buffer.
 *
 * §1.6: pure DSP/output transport — no keys, no crypto, no game content. Tests
 * use only synthetic PCM generated in-test.
 */

/** Control header slot indices (Int32 each). */
export const RING_IDX_READ = 0; // consumer-published read position, [0, 2*capacity)
export const RING_IDX_WRITE = 1; // producer-published write position, [0, 2*capacity)

/** Header size: 16 Int32 slots (64 bytes) — only 2 are used today; the rest is
 *  reserved padding so the payload starts cache-line aligned and there is room
 *  to grow (e.g. an underrun counter) without changing the payload offset. */
export const HEADER_I32 = 16;
export const HEADER_BYTES = HEADER_I32 * 4;

/** Sample capacity (number of Int16 elements the payload can hold) for a ring
 *  backed by a SharedArrayBuffer of `byteLength` bytes. */
export function ringCapacitySamples(byteLength: number): number {
  return (byteLength - HEADER_BYTES) >> 1; // 2 bytes per Int16 sample
}

/**
 * A single-producer/single-consumer S16 ring over a SharedArrayBuffer. Construct
 * one in each context (producer in the CPU worker, consumer in the worklet) over
 * the SAME SharedArrayBuffer; the control header coordinates them via Atomics.
 */
export class AudioRing {
  private readonly header: Int32Array;
  private readonly data: Int16Array;
  /** Number of Int16 samples the payload holds. */
  readonly capacity: number;
  /** The index-space modulus (2 * capacity); indices live in [0, modulus). */
  private readonly modulus: number;

  constructor(sab: SharedArrayBuffer) {
    if (sab.byteLength < HEADER_BYTES + 2) {
      throw new RangeError(
        `AudioRing SharedArrayBuffer too small: ${sab.byteLength} bytes`,
      );
    }
    this.header = new Int32Array(sab, 0, HEADER_I32);
    this.capacity = ringCapacitySamples(sab.byteLength);
    this.modulus = this.capacity * 2;
    this.data = new Int16Array(sab, HEADER_BYTES, this.capacity);
  }

  /**
   * Reset the ring to empty. Call once from the OWNER (the allocator on the main
   * thread, before either worker touches it). Not safe to call concurrently with
   * push/pull.
   */
  reset(): void {
    Atomics.store(this.header, RING_IDX_READ, 0);
    Atomics.store(this.header, RING_IDX_WRITE, 0);
  }

  /** Number of samples currently readable. */
  available(): number {
    const w = Atomics.load(this.header, RING_IDX_WRITE);
    const r = Atomics.load(this.header, RING_IDX_READ);
    return (w - r + this.modulus) % this.modulus;
  }

  /** Number of samples the producer may still write before the ring is full. */
  free(): number {
    return this.capacity - this.available();
  }

  /**
   * Producer: append up to `samples.length` Int16 samples. If the ring lacks
   * room, the overflow is DROPPED (the newest samples are not written) so the
   * consumer never reads stale/torn data. Returns the number of samples actually
   * written.
   */
  push(samples: Int16Array): number {
    const want = samples.length;
    if (want === 0) return 0;
    const room = this.free();
    const n = want < room ? want : room;
    if (n === 0) return 0;

    const w = Atomics.load(this.header, RING_IDX_WRITE);
    const start = w % this.capacity;
    const first = Math.min(n, this.capacity - start);
    this.data.set(samples.subarray(0, first), start);
    if (first < n) {
      this.data.set(samples.subarray(first, n), 0);
    }
    // Publish the new write index AFTER the payload writes are visible.
    Atomics.store(this.header, RING_IDX_WRITE, (w + n) % this.modulus);
    return n;
  }

  /**
   * Consumer: fill `out` with the next samples. Reads up to `out.length`
   * available samples; any remaining tail is filled with SILENCE (0) — an
   * underflow yields silence, never stale audio. Returns the number of REAL
   * (non-silence) samples copied.
   */
  pull(out: Int16Array): number {
    const want = out.length;
    if (want === 0) return 0;
    const avail = this.available();
    const n = want < avail ? want : avail;

    if (n > 0) {
      const r = Atomics.load(this.header, RING_IDX_READ);
      const start = r % this.capacity;
      const first = Math.min(n, this.capacity - start);
      out.set(this.data.subarray(start, start + first), 0);
      if (first < n) {
        out.set(this.data.subarray(0, n - first), first);
      }
      // Publish the advanced read index AFTER the copy-out.
      Atomics.store(this.header, RING_IDX_READ, (r + n) % this.modulus);
    }
    // Underflow: zero the unfilled tail (silence).
    if (n < want) {
      out.fill(0, n);
    }
    return n;
  }

  /**
   * Consumer: copy up to `out.length` samples starting `offset` samples PAST the
   * current read position into `out` WITHOUT advancing the read index (a
   * look-ahead). Any tail it can't satisfy is filled with SILENCE (0). Returns
   * the number of REAL (non-silence) samples copied.
   *
   * This exists so a resampler can READ a few source frames beyond the ones it
   * will actually CONSUME this quantum — its last output frame's upper
   * interpolation neighbour can lie one source frame past the consumed count
   * (especially when UPSAMPLING, step < 1). The consumer peeks the wider
   * interpolation window, then {@link advance}s only by the frames it truly
   * consumed, so the read-ahead frame is re-read (not dropped) next quantum. The
   * SPSC contract is preserved: peek never moves the read index, so it can't
   * race the producer's published write index.
   */
  peek(out: Int16Array, offset = 0): number {
    const want = out.length;
    if (want === 0) return 0;
    const avail = this.available();
    // Samples readable starting `offset` past the read position.
    const readable = offset >= avail ? 0 : avail - offset;
    const n = want < readable ? want : readable;

    if (n > 0) {
      const r = (Atomics.load(this.header, RING_IDX_READ) + offset) % this.capacity;
      const first = Math.min(n, this.capacity - r);
      out.set(this.data.subarray(r, r + first), 0);
      if (first < n) {
        out.set(this.data.subarray(0, n - first), first);
      }
    }
    if (n < want) {
      out.fill(0, n);
    }
    return n;
  }

  /**
   * Consumer: advance the read index by up to `count` samples WITHOUT copying
   * (drop/consume them). Advances by min(count, available) so it never overruns
   * the producer. Returns the number of samples actually consumed. Pairs with
   * {@link peek}: peek the interpolation window, advance by the consumed count.
   */
  advance(count: number): number {
    if (count <= 0) return 0;
    const avail = this.available();
    const n = count < avail ? count : avail;
    if (n > 0) {
      const r = Atomics.load(this.header, RING_IDX_READ);
      Atomics.store(this.header, RING_IDX_READ, (r + n) % this.modulus);
    }
    return n;
  }
}
